import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject, combineLatest, map } from 'rxjs';

import { AccountFacade } from '@mode/accounts/contract';
import { OrgTypes, UserTypes } from '@mode/shared/contract-common';
import { ConfirmDialogService } from '@mode/shared/ui';
import { getAppName, LegacyRouterService } from '@mode/shared/util-js';
import { BugsnagService } from '@mode/shared/util-error-handling';
import { AnalyticsEvent, AnalyticsService, RunMethodType } from '@mode/shared/util-segment';
import { ToastService } from '@mode/shared/util-toast';
import { takeUntil, tap } from 'rxjs/operators';
import { EConnectDatabaseElementId } from '../connect-database-element-id.enum';
import { ContextDialogService } from '../context-dialog/context-dialog.service';
import { EmailAdminDialogService } from '../email-admin-dialog/email-admin-dialog.service';
import { ThoughtspotIntegrationService } from '@mode/shared/util-thoughtspot-integrated';

@Component({
  selector: 'mode-nav-account-menu',
  templateUrl: './nav-account-menu.component.html',
  styleUrls: ['./nav-account-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavAccountMenuComponent implements OnInit, OnDestroy {
  @Input() currentUser: UserTypes.User | undefined;
  @Input()
  set currentOrg(value: OrgTypes.Organization | undefined) {
    if (!value) {
      return;
    }
    this._currentOrg = value;
    this.appName = getAppName({ isFree: value.isFreePlan });
    this.isCommunityOrg = value.user;
    this.canConnectDb = value.membershipType === 'admin' || !value.adminDataSourceConnectionsOnly;
  }
  get currentOrg(): OrgTypes.Organization | undefined {
    return this._currentOrg;
  }
  private _currentOrg: OrgTypes.Organization | undefined;
  private _destroy$ = new Subject<void>();
  protected appName: string | undefined;
  public isCommunityOrg = false;
  public canConnectDb = false;
  public hasMemberOrgs$ = this._accountFacade.hasMemberOrgs$;
  public isNotThoughtspotIntegrated$ = this._thoughtspotIntegrationService.currOrgIntegrated$.pipe(map((v) => !v));
  public numberOfSeats = 3;
  public isAnalystStudio$ = this._thoughtspotIntegrationService.currOrgIntegrated$;
  public readonly connectDatabaseElementId = EConnectDatabaseElementId.NAV_ACCOUNT_MENU_CTA;

  constructor(
    private _analyticsService: AnalyticsService,
    private _accountFacade: AccountFacade,
    private _bugsnagService: BugsnagService,
    private _contextDialogService: ContextDialogService,
    private _confirmDialogService: ConfirmDialogService,
    private _emailAdminDialogService: EmailAdminDialogService,
    private _legacyRouterService: LegacyRouterService,
    private _toastService: ToastService,
    private _thoughtspotIntegrationService: ThoughtspotIntegrationService
  ) {}

  ngOnInit(): void {
    combineLatest([
      this._accountFacade.isEmailAdminSending$,
      this._accountFacade.isEmailAdminSuccess$,
      this._accountFacade.isEmailAdminError$,
    ])
      .pipe(
        tap(([isSending, isSuccess, error]) => {
          if (!isSending && !isSuccess && error) {
            this._bugsnagService.notify(error);
            this._toastService.error('Email failed to send');
          } else if (!isSending && isSuccess && !error) {
            this._toastService.success('Email sent!');
          }
        }),
        takeUntil(this._destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  openContextDialog(): void {
    this._accountFacade.loadUserOrgs();
    this._contextDialogService.open({
      currentUser: this.currentUser,
      currentOrg: this.currentOrg,
      hasMemberOrgs$: this._accountFacade.hasMemberOrgs$,
      isUserOrgsComplete$: this._accountFacade.isUserOrgsComplete$,
      isUserOrgsLoading$: this._accountFacade.isUserOrgsLoading$,
      userOrgs$: this._accountFacade.userOrgs$,
    });
  }

  openConnectDataSourceDialog(): void {
    if (this._currentOrg?.id) {
      this._analyticsReport(this.connectDatabaseElementId, this._currentOrg.id.toString());
    }

    this._confirmDialogService.open({
      dialogTitle: 'Connect your Data Source',
      dialogText: `To work with a privately connected data source, you'll need to switch to a Workspace first. Workspaces help teams work together on analysis and share data privately.`,
      confirmText: 'Switch to Workspace',
      confirmButtonColor: 'default',
      confirmAction: () => this.openContextDialog(),
    });
  }

  openSwitchToModeStudioDialog(): void {
    if (this._currentOrg?.id) {
      this._analyticsReport(this.connectDatabaseElementId, this._currentOrg.id.toString());
    }

    this._confirmDialogService.open({
      dialogTitle: 'Switch to Mode Studio',
      dialogText: `Before you connect a database, you'll need to create a free Mode Studio workspace. This will let you query and share data privately with others. You can return to the Mode Community at any point.`,
      confirmText: 'Switch to Mode Studio',
      confirmButtonColor: 'default',
      confirmAction: () =>
        this._legacyRouterService.navigateByUrl(
          `${this.currentOrg?.links.newOrganization ?? ''}?trk_element_id=${this.connectDatabaseElementId}`
        ),
    });
  }

  openEmailAnAdminDialog(): void {
    this._accountFacade.loadAdminsForEmailForm();
    this._emailAdminDialogService.open({
      admins$: this._accountFacade.admins$,
      confirmAction: ({ adminId, message }) => this._accountFacade.emailAdmin(adminId, message),
    });
  }

  private _analyticsReport(elementId: number, accountId: string): void {
    this._analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.ConnectDbIntentSignaled, {
      interaction_type: 'click',
      element_id: elementId,
      account_id: accountId,
    });
  }
}
